import { AuthProvider } from '@doss/common-all'
import config from '../config/config'

/**
 * Type guard to check if the auth provider is WorkOS (uses local emulator in dev).
 * Use this instead of inline checks for consistency.
 * Note: In dev, WorkOS automatically uses local emulator; in production, it uses real WorkOS.
 *
 * @param provider - The auth provider to check
 * @returns true if provider is 'workos'
 *
 * @example
 * if (isWorkOSProvider(config.authProvider)) {
 *   // WorkOS-specific logic
 * }
 */
export const isWorkOSProvider = (provider: AuthProvider): boolean => {
  return provider === 'workos'
}

/**
 * Type guard to check if the auth provider is Firebase (legacy).
 * Use this instead of inline checks for consistency.
 *
 * @param provider - The auth provider to check
 * @returns true if provider is 'firebase'
 *
 * @example
 * if (isFirebaseProvider(config.authProvider)) {
 *   // Firebase-specific logic
 * }
 */
export const isFirebaseProvider = (provider: AuthProvider): boolean => {
  return provider === 'firebase'
}

/**
 * Gets the current auth provider from config.
 * Convenience helper to avoid importing config directly.
 *
 * @returns The current AuthProvider value
 */
export const getAuthProvider = (): AuthProvider => {
  return config.authProvider
}

/**
 * Hook that returns the current auth provider type and convenience flags.
 * Use this in components that need to branch on auth provider.
 *
 * @returns Object with isWorkOS, isFirebase flags and the provider value
 *
 * @example
 * const { isWorkOS, isFirebase, provider } = useAuthProviderType()
 * if (isWorkOS) {
 *   // WorkOS-specific rendering
 * }
 */
export const useAuthProviderType = () => {
  const provider = config.authProvider

  return {
    isWorkOS: isWorkOSProvider(provider),
    isFirebase: isFirebaseProvider(provider),
    provider,
  }
}
